import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { StateValues } from 'react-use-form-state';
import Translate from 'ui/atoms/translate';
import { Modal, ModalHeader, ModalContent, ModalFooter, ModalFooterButtons, makeModalForm } from 'ui/molecules/modal';
import Icon from 'src/ui/atoms/icon';
import Tooltip, { TooltipPosition } from 'src/ui/atoms/tooltip';
import useIsMedia, { DEVICES } from 'src/ui/hooks/use-is-media';
import { handleError } from 'src/ui/helper/error-handling';
import useTranslate from 'src/ui/hooks/use-translate';
import { AdminApi, AdminGroupRequest, DropdownElement, OrganizationPermissionsEnum } from 'src/api';
import useApiCall from 'src/hooks/use-api-call';
import { debounce } from 'lodash';
import { currentUserHasManageUsersAndGroupsPermission } from 'src/apps/issuer/helpers/userPermissions';
import { useCurrentUserSelector } from 'src/core/auth/hooks';

interface AddGroupModalProps {
  onSaveGroup: () => void;
  onClose: () => void;
}

export interface AddGroupFields {
  name: string;
  organization: string;
}

export const Form = makeModalForm<AddGroupFields>();

const AddGroupModal: FunctionComponent<AddGroupModalProps> = ({ onSaveGroup, onClose }) => {
  const [organizations, setOrganizations] = useState<DropdownElement[]>([]);

  const { makeAuthenticatedApi, withApi, error } = useApiCall();
  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);
  const isPhone = useIsMedia(DEVICES.phone);
  const { currentUser } = useCurrentUserSelector();

  const { getErrorForField: getNameError } = handleError({
    error,
    translate: useTranslate(),
    specialCodes: {
      unique: {
        field: 'name',
        translateKey: 'addGroupModal.fields.name.uniqueError',
      },
    },
  });

  const fetchOrganizations = useCallback(
    (searchQuery: string = '', action?: string) => {
      withApi(async () => {
        if (action === 'menu-close' && searchQuery === '') {
          return;
        }
        const response = await adminApi.adminOrganizationsDropdownList({
          search: searchQuery,
          limit: 100,
          ...(currentUserHasManageUsersAndGroupsPermission(currentUser?.permissions || [])
            ? {}
            : { permissions: OrganizationPermissionsEnum.MANAGE_ORGANIZATION_USERS_AND_GROUPS }),
        });
        setOrganizations(response.results || []);
      });
    },
    [withApi, adminApi],
  );

  const searchOrganizationValue = useCallback(
    (value: string, action?: string) => {
      fetchOrganizations(value, action);
    },
    [fetchOrganizations],
  );

  const debouncedOrganizationSearch = useMemo(() => debounce(searchOrganizationValue, 500), [searchOrganizationValue]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  const onSubmit = useCallback((fieldsValues: StateValues<AddGroupFields>) => {
    const params: AdminGroupRequest = {
      name: fieldsValues.name,
      organization: fieldsValues.organization,
    };

    withApi(async () => {
      await adminApi.adminGroupsCreate({ adminGroupRequest: params });
      onSaveGroup();
    });
  }, []);

  return (
    <Modal onClose={onClose}>
      <Form
        i18nKey="addGroupModal"
        onSubmit={(fieldValues: StateValues<AddGroupFields>) => {
          const values = {
            ...fieldValues,
          };
          onSubmit(values);
        }}
        error={error}
      >
        <ModalHeader>
          <Translate name="addGroupModal.title" />
        </ModalHeader>
        <ModalContent>
          <Form.Group
            name="name"
            required={true}
            fieldError={getNameError('name')}
            requiredError={<Translate name="addGroupModal.fields.name.requiredError" />}
            tooltip={
              <Tooltip
                content={<Translate name="addGroupModal.fields.name.tooltip" />}
                position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
              >
                <Icon name="question" size="large" color="info" padded="none" />
              </Tooltip>
            }
          >
            <Form.Input />
          </Form.Group>
          <Form.Group
            name="organization"
            required={true}
            requiredError={<Translate name="addGroupModal.fields.organization.requiredError" />}
            tooltip={
              <Tooltip
                content={<Translate name="addGroupModal.fields.organization.tooltip" />}
                position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                maxWidth={200}
              >
                <Icon name="question" circular={true} size="large" color="info" padded="none" />
              </Tooltip>
            }
          >
            <Form.Select
              options={organizations.map(({ id, name }) => ({
                value: id,
                label: name,
              }))}
              onInputChange={debouncedOrganizationSearch}
            />
          </Form.Group>
        </ModalContent>
        <ModalFooter>
          <ModalFooterButtons
            actionButtons={[
              {
                name: 'cancel',
                content: <Translate name="common.cancel" />,
                size: 'large',
                onClick: () => onClose(),
              },
              {
                name: 'add',
                content: <Translate name="common.add" />,
                variant: 'primary',
                type: 'submit',
                size: 'large',
              },
            ]}
          />
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default AddGroupModal;
